import { isI18nKeyExists } from "@helpers/common";
import { groupTagsByType } from "@helpers/content/tag";
import type { SessionConfigFiltersType } from "@memorang/constants/config/schema";
import type { MixedItemDist } from "@memorang/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { titleCase } from "@memorang/helpers/common";
import pluralize from "pluralize";
import {
	calculateProficiencyBasedDistribution,
	groupSelectedTagsByTopLevelType,
} from "../helpers/filter";
import { TAG_TYPES } from "../helpers/session-filters";

type MixedItemDistResponse = MixedItemDist;

type Params = {
	studyPackDistribution: MixedItemDistResponse | undefined;
	hasAccess: boolean;
	maxFilterDepth: number;
	sessionConfigFilters: SessionConfigFiltersType;
	showPointsBasedScore?: boolean;
};

export const useSessionConfigSections = ({
	studyPackDistribution,
	hasAccess,
	maxFilterDepth,
	sessionConfigFilters,
	showPointsBasedScore,
}: Params) => {
	const { t } = useTranslation();

	const mappedMixedItemDistResponse = useMemo(
		() =>
			studyPackDistribution
				? {
						...studyPackDistribution,
						tags: showPointsBasedScore
							? calculateProficiencyBasedDistribution(
									studyPackDistribution.tags,
									sessionConfigFilters,
								).mappedTags
							: studyPackDistribution.tags,
					}
				: undefined,
		[studyPackDistribution, showPointsBasedScore, sessionConfigFilters],
	);

	const tagsByType = useMemo(
		() =>
			groupTagsByType(
				mappedMixedItemDistResponse?.tags,
				"Filter",
				mappedMixedItemDistResponse?.freeTagIds ?? [],
				hasAccess,
				undefined,
				maxFilterDepth,
			),
		[
			mappedMixedItemDistResponse?.tags,
			mappedMixedItemDistResponse?.freeTagIds,
			hasAccess,
			maxFilterDepth,
		],
	);

	const sectionTags = useMemo(() => {
		const TYPE_ORDER: Record<string, number> = {
			[TAG_TYPES.skill]: 0,
			[TAG_TYPES.questionType]: 1,
			[TAG_TYPES.proficiency]: 2,
		};

		return Object.entries(tagsByType)
			.sort(([a], [b]) => (TYPE_ORDER[a] ?? 99) - (TYPE_ORDER[b] ?? 99))
			.map(([rawType, tags]) => ({
				title: isI18nKeyExists(`sessionConfig.${rawType}`)
					? t(`sessionConfig.${rawType}`)
					: titleCase(pluralize(rawType)),
				tags,
			}));
	}, [tagsByType, t]);

	// Grouping runs against the full response tree, not the view-filtered
	// tagsByType: recommended-session tag ids can reference tags whose type is
	// filtered out of the visible sections, and their subtrees are depth-truncated.
	const groupSelectedTagsByType = (selectedTagIds: number[]) => {
		return groupSelectedTagsByTopLevelType(
			selectedTagIds,
			mappedMixedItemDistResponse?.tags ?? [],
		);
	};

	return {
		mappedMixedItemDistResponse,
		tagsByType,
		sectionTags,
		groupSelectedTagsByType,
	};
};
